import ThemedElement from "shared/components/ThemedElement";
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Snackbar,
  Alert,
  Box,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
} from "@mui/material";
import {
  CheckCircle,
  Warning,
  Refresh,
  Error as ErrorIcon,
  Info,
  Timer,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";

// Material Dashboard 2 React components
import DashboardLayout from "app/layout/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/layout/DashboardNavbar";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";

// Componentes personalizados
import {
  getCommerceWhatsAppAttributesService,
  getCommerceWhatsAppAddService,
  createCommerceWhatsAppService,
  updateCommerceWhatsAppService,
} from "modules/jarvis/services/whatsappService";
import { useWhatsappCopJarvis } from "./hooks/useWhatsappCopJarvis";
import HeaderWhatsappCop from "./components/HeaderWhatsappCop";
import TableWhatsappCop from "./components/TableWhatsappCop";
import CreateWhatsappCopDialog from "./components/CreateWhatsappCopDialog";
import EditWhatsappCopDialog from "./components/EditWhatsappCopDialog";

const WhatsappCop = () => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    action: null,
    autoHideDuration: 6000,
  });

  // Estado para el diálogo de creación
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [attributes, setAttributes] = useState({
    groupnoc: [],
    workflows: [],
  });
  const [availableComercios, setAvailableComercios] = useState([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [creatingConfig, setCreatingConfig] = useState(false);

  // Estado para el diálogo de edición
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [updatingConfig, setUpdatingConfig] = useState(false);

  // Usar hook personalizado
  const {
    configuraciones,
    loading,
    error,
    stats,
    showNoData,
    filtros,
    gruposNOC,
    refreshData,
    setFiltro,
    hasData,
    totalConfiguraciones,
    isLoading,
  } = useWhatsappCopJarvis();

  // Cargar atributos y comercios disponibles
  const loadAttributes = useCallback(async () => {
    setLoadingAttributes(true);
    try {
      const [attrsResult, addResult] = await Promise.all([
        getCommerceWhatsAppAttributesService(),
        getCommerceWhatsAppAddService(),
      ]);

      if (attrsResult.success) {
        setAttributes(attrsResult.data);
      } else {
        showError("Error al cargar atributos: " + attrsResult.error);
      }

      if (addResult.success) {
        setAvailableComercios(addResult.data);
      } else {
        showError("Error al cargar comercios disponibles: " + addResult.error);
      }
    } catch (error) {
      console.error("Error cargando atributos:", error);
      showError("Error al cargar datos");
    } finally {
      setLoadingAttributes(false);
    }
  }, []);

  // Crear nueva configuración
  const handleCreateConfig = async (formData) => {
    setCreatingConfig(true);
    try {
      const result = await createCommerceWhatsAppService(formData);

      if (result.success) {
        showSuccess("Configuración creada exitosamente");
        await refreshData();
        return result;
      } else {
        showError(result.error || "Error al crear la configuración");
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Error en creación:", error);
      showError(error.message || "Error al crear la configuración");
      throw error;
    } finally {
      setCreatingConfig(false);
    }
  };

  // Actualizar configuración
  const handleUpdateConfig = async (formData) => {
    setUpdatingConfig(true);
    try {
      const result = await updateCommerceWhatsAppService(formData);

      if (result.success) {
        showSuccess("Configuración actualizada exitosamente");
        await refreshData();
        return result;
      } else {
        showError(result.error || "Error al actualizar la configuración");
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Error en actualización:", error);
      showError(error.message || "Error al actualizar la configuración");
      throw error;
    } finally {
      setUpdatingConfig(false);
    }
  };

  // Abrir diálogo de creación
  const handleCreateClick = () => {
    loadAttributes();
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  // Abrir diálogo de edición
  const handleEditClick = (configuracion) => {
    setSelectedConfig(configuracion);
    loadAttributes();
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedConfig(null);
  };

  // ========== MANEJO DE NOTIFICACIONES ==========
  const showNotification = (message, severity = "success", options = {}) => {
    setSnackbar({
      open: true,
      message,
      severity,
      action: options.action || null,
      autoHideDuration: options.persist ? null : options.duration || 6000,
    });
  };

  const showSuccess = (message, options = {}) => {
    showNotification(message, "success", options);
  };

  const showError = (message, options = {}) => {
    showNotification(message, "error", options);
  };

  const showWarning = (message, options = {}) => {
    showNotification(message, "warning", options);
  };

  const showInfo = (message, options = {}) => {
    showNotification(message, "info", options);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // ========== MANEJADORES DE EVENTOS ==========
  const handleRefresh = async () => {
    try {
      showInfo("Actualizando datos...", { duration: 2000 });
      await refreshData();
      showSuccess("Datos actualizados correctamente");
    } catch (error) {
      showError(`Error al actualizar datos: ${error.message}`, {
        action: (
          <IconButton size="small" color="inherit" onClick={handleRefresh}>
            <Refresh fontSize="small" />
          </IconButton>
        ),
      });
    }
  };

  const handleFiltroChange = (tipo, valor) => {
    setFiltro(tipo, valor);
  };

  const handleRetryLoad = (accion) => {
    if (accion === "reintentar") {
      showInfo("Reintentando carga de datos...", { duration: 2000 });
      handleRefresh();
    } else if (accion === "limpiarBusqueda") {
      handleFiltroChange("busqueda", "");
    } else if (accion === "limpiarActivo") {
      handleFiltroChange("activo", "todos");
    } else if (accion === "limpiarPulso") {
      handleFiltroChange("pulso", "todos");
    } else if (accion === "limpiarGrupo") {
      handleFiltroChange("grupo", "todos");
    }
  };

  // ========== EFECTOS ==========
  useEffect(() => {
    if (error && !showNoData) {
      showError(error, {
        action: (
          <IconButton size="small" color="inherit" onClick={handleRefresh}>
            <Refresh fontSize="small" />
          </IconButton>
        ),
      });
    }
  }, [error, showNoData]);

  useEffect(() => {
    if (showNoData) {
      showWarning("La carga de datos está tomando más tiempo de lo esperado.", {
        persist: false,
        duration: 8000,
        action: (
          <IconButton size="small" color="inherit" onClick={handleRefresh}>
            <Refresh fontSize="small" />
          </IconButton>
        ),
      });
    }
  }, [showNoData]);

  useEffect(() => {
    if (hasData && !isLoading && !error && !showNoData) {
      showSuccess(`${configuraciones.length} configuraciones cargadas correctamente`, {
        duration: 3000,
      });
    }
  }, [hasData, configuraciones.length, isLoading, error, showNoData]);

  // ========== RENDERIZADO ==========
  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* Barra de progreso para carga */}
      {(isLoading || loadingAttributes || creatingConfig || updatingConfig) && (
        <LinearProgress
          color="success"
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            backgroundColor: "#075e54",
            "& .MuiLinearProgress-bar": { backgroundColor: "#25D366" },
          }}
        />
      )}

      {/* Notificación Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          action={snackbar.action}
          sx={{ width: "100%", maxWidth: "500px", boxShadow: 3, borderRadius: "8px" }}
          iconMapping={{
            success: <CheckCircle fontSize="inherit" />,
            error: <ErrorIcon fontSize="inherit" />,
            warning: <Warning fontSize="inherit" />,
            info: <Info fontSize="inherit" />,
          }}
        >
          <MDTypography variant="body2" fontWeight="medium">
            {snackbar.message}
          </MDTypography>
        </Alert>
      </Snackbar>

      {/* Contenido principal */}
      <MDBox py={3}>
        {/* Sección de header con filtros */}
        <MDBox mb={4}>
          <HeaderWhatsappCop
            onRefresh={handleRefresh}
            onCreateClick={handleCreateClick}
            loading={isLoading}
            filtros={filtros}
            onFiltroChange={handleFiltroChange}
            gruposNOC={gruposNOC}
          />
        </MDBox>

        {(hasData || error || showNoData) && (
          <MDBox mb={3}>
            <Divider
              sx={{
                borderColor: error ? "error.light" : showNoData ? "warning.light" : "#075e54",
                borderWidth: 1,
                opacity: hasData ? 1 : 0.5,
              }}
            />
          </MDBox>
        )}

        <MDBox>
          <Card
            sx={{
              mb: 3,
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 4px 20px 0 rgba(0,0,0,0.1)",
              border: error
                ? "1px solid #f44336"
                : showNoData
                ? "1px solid #ff9800"
                : "1px solid #e0e0e0",
            }}
          >
            <MDBox p={3} bgcolor="background.paper">
              <TableWhatsappCop
                configuraciones={configuraciones}
                loading={isLoading}
                error={error}
                showNoData={showNoData}
                onError={handleRetryLoad}
                totalConfiguraciones={totalConfiguraciones}
                filtros={filtros}
                stats={stats}
                onEditClick={handleEditClick}
              />
            </MDBox>
          </Card>

          {/* Panel de estado del sistema */}
          <MDBox
            mt={3}
            p={2.5}
            bgcolor="background.paper"
            borderRadius="8px"
            sx={{
              border: "1px solid",
              borderColor: error ? "error.light" : showNoData ? "warning.light" : "divider",
              backgroundColor: error
                ? "rgba(244, 67, 54, 0.05)"
                : showNoData
                ? "rgba(255, 152, 0, 0.05)"
                : "background.paper",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <MDBox display="flex" alignItems="center" gap={1.5}>
                  {error ? (
                    <ErrorIcon fontSize="small" color="error" />
                  ) : showNoData ? (
                    <Timer fontSize="small" color="warning" />
                  ) : (
                    <CheckCircle fontSize="small" color="success" />
                  )}
                  <MDTypography variant="body2" color="textSecondary">
                    Estado del sistema:{" "}
                    <ThemedElement
                      component="strong"
                      style={{
                        color: error ? "#f44336" : showNoData ? "#ff9800" : "#4caf50",
                      }}
                    >
                      {error ? "Con errores" : showNoData ? "Carga lenta" : "Operativo"}
                    </ThemedElement>
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={4}>
                <MDBox display="flex" alignItems="center" gap={1.5}>
                  <WhatsAppIcon fontSize="small" color="success" />
                  <MDTypography variant="body2" color="textSecondary">
                    Total configuraciones:{" "}
                    <ThemedElement component="strong" style={{ color: "#075e54" }}>
                      {stats.total || 0}
                    </ThemedElement>
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={4}>
                <MDTypography variant="body2" color="textSecondary">
                  Última actualización: <strong>{new Date().toLocaleTimeString()}</strong>
                </MDTypography>
              </Grid>
            </Grid>
          </MDBox>

          {hasData && !showNoData && !error && (
            <MDBox
              mt={2}
              p={2}
              bgcolor="rgba(7, 94, 84, 0.05)"
              borderRadius="8px"
              border="1px solid rgba(7, 94, 84, 0.1)"
            >
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <MDBox textAlign="center">
                    <MDTypography variant="h6" color="#075e54" fontWeight="bold">
                      {stats.activos || 0}
                    </MDTypography>
                    <MDTypography variant="caption" color="textSecondary">
                      Activos
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <MDBox textAlign="center">
                    <MDTypography variant="h6" color="#075e54" fontWeight="bold">
                      {stats.conPulso || 0}
                    </MDTypography>
                    <MDTypography variant="caption" color="textSecondary">
                      Con Pulso
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <MDBox textAlign="center">
                    <MDTypography variant="h6" color="#075e54" fontWeight="bold">
                      {stats.conTerminal || 0}
                    </MDTypography>
                    <MDTypography variant="caption" color="textSecondary">
                      Con Terminal
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <MDBox textAlign="center">
                    <MDTypography variant="h6" color="#075e54" fontWeight="bold">
                      {stats.gruposNOC || 0}
                    </MDTypography>
                    <MDTypography variant="caption" color="textSecondary">
                      Grupos NOC
                    </MDTypography>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      {/* Diálogo de creación */}
      <CreateWhatsappCopDialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        onCreate={handleCreateConfig}
        attributes={attributes}
        availableComercios={availableComercios}
        loading={loadingAttributes || creatingConfig}
      />

      {/* Diálogo de edición */}
      <EditWhatsappCopDialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        onUpdate={handleUpdateConfig}
        configuracion={selectedConfig}
        attributes={attributes}
        loading={loadingAttributes || updatingConfig}
      />
    </DashboardLayout>
  );
};

export default WhatsappCop;
